import { Grid, Paper, Theme, Typography, Box, Button, CircularProgress } from '@mui/material';
import AcceptedIcon from 'components/icons/CustomStatuses/AcceptedIcon';
import HiddenIcon from 'components/icons/CustomStatuses/HiddenIcon';
import RejectedIcon from 'components/icons/CustomStatuses/RejectedIcon';
import GVCheckbox from 'components/lib/GVCheckbox/GVCheckbox';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import { getStyleVariables, menuLightGrey } from 'styles/vars';
import { makeStyles } from 'tss-react/mui';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { useEffect, useState } from 'react';
import { CustomStatus, useCustomStatuses, useUpdateCustomStatus } from 'store/queries/organizations/useCustomStatuses';
import { useDispatch } from 'react-redux';
import { updateOrganization } from 'store/myAccount/requests';
import { useUserStateStore } from 'zstore/userStateStore';
import { useOrganizationQuery } from 'store/queries/organizations/useOrganizationQuery';
import { getOrganizationId } from 'utils/auth';
import { useTracker } from 'components/Tracker/TrackerProvider';
import styles from './CustomStatuses.module.css';

const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    paper: {
      width: '100%',
      height: 'calc(100vh - 250px)',
      padding: '25px 28px',
      backgroundColor: styleVariables.colors.menuLightGrey,
      '--gds-spacing-field-container-margin': 'var(--gds-spacing-xs) var(--gds-spacing-sm)',
      '--gds-size-field-height': '40px',
      '--gds-color-field-text-default': 'var(--gds-color-text-tertiary)',
      '--gds-color-field-text-focus': 'var(--gds-color-text-primary)',
      '--gds-typography-field-font':
        'var(--gds-typography-font-weight-semibold) var(--gds-typography-font-size-xs)/var(--gds-typography-line-height-sm) var(--gds-typography-font-family-default)',
      '--gds-spacing-field-value-padding': 'var(--gds-spacing-md) var(--gds-spacing-sm)',
      '--gds-color-field-border-default': 'var(--gds-color-cool-gray-300)',
      '--gds-color-field-border-focus': 'var(--gds-color-cool-gray-100)',
      '--gds-display-field-group': 'grid',
      '--gds-size-field-width': '185px',
      // Display 2px thick border when focused. This is 1px added to the already existing 1px border.
      '[data-state="focused"]': {
        '--gds-color-field-outline': 'var(--gds-color-cool-gray-100)',
        '--gds-outline-field-style': 'solid',
        '--gds-outline-field-width': '1px',
      },
    },
    hotkeyBox: {
      display: 'flex',
      padding: '5px 6px 5px 7px',
      margin: 'auto auto auto var(--gds-spacing-sm)',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '10px',
      borderRadius: '4px',
      border: '1px solid rgba(255, 255, 255, 0.38)',
      background: menuLightGrey,
    },
    hotkeyText: {
      color: 'rgba(255, 255, 255, 0.38)',
      fontWeight: 400,
      lineHeight: '18px',
      marginLeft: 'var(--gds-spacing-lg)',
    },
    checkbox: {
      margin: ' auto auto auto 8px',
    },
    reportBox: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      borderRadius: '4px',
      background: 'rgba(99, 99, 99, 0.21)',
      color: '#A3A3A3',
      padding: '2px 5px 3px 5px',
      width: 'fit-content',
    },
    statusRequiredText: {
      color: '#A3A3A3',
      fontSize: '11px',
      fontWeight: 600,
      lineHeight: '16px',
    },
    secondaryText: {
      color: 'rgba(255, 255, 255, 0.38)',
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
    },
    statusRequiredGridItem: {
      paddingTop: '0 !important',
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
      marginLeft: '8px',
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-start',
      gap: '8px',
      marginTop: '36px',
    },
  };
});

const iconMap = {
  First: { Icon: AcceptedIcon, hotkey: 'F1' },
  Second: { Icon: RejectedIcon, hotkey: 'F2' },
  Third: { Icon: HiddenIcon, hotkey: 'F3' },
};

const transformStatuses = (statuses: CustomStatus[]): Record<string, CustomStatus> => {
  return statuses?.reduce(
    (result, status) => {
      result[status.id] = status;
      return result;
    },
    {} as Record<string, CustomStatus>,
  );
};

const CustomStatuses = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const tracker = useTracker();
  const { data: statuses, isFetching: isFetchingStatuses } = useCustomStatuses();
  const { mutate: updateCustomStatus } = useUpdateCustomStatus();
  const { user } = useUserStateStore();
  const orgId = getOrganizationId(user);
  const {
    data: initialOrgData,
    isFetching: isFetchingOrg,
    refetch: refreshOrgData,
  } = useOrganizationQuery(orgId, {
    includeUsers: false,
  });

  const [currentCustomStatuses, setCurrentCustomStatuses] = useState(transformStatuses(statuses || []));
  const [currentForceReview, setCurrentForceReview] = useState(false);
  const [changedIds, setChangedIds] = useState<string[]>([]);
  const unsavedChanges =
    JSON.stringify(currentCustomStatuses) !== JSON.stringify(transformStatuses(statuses as CustomStatus[])) ||
    (initialOrgData && currentForceReview !== initialOrgData?.forceReview);
  const MAX_LENGTH = 18;
  const [helperTexts, setHelperTexts] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isFetchingStatuses) {
      setCurrentCustomStatuses(transformStatuses(statuses || []));
    }
  }, [isFetchingStatuses]);

  useEffect(() => {
    if (!isFetchingOrg && initialOrgData) {
      setCurrentForceReview(initialOrgData.forceReview || false);
    }
  }, [isFetchingOrg]);

  const updateChangedIds = (id: string) => {
    if (!changedIds.includes(id)) {
      const newChangedIds = [...changedIds, id];
      setChangedIds(newChangedIds);
    }
  };

  const handleCheckboxChange = (id: string) => {
    updateChangedIds(id);

    if (!currentCustomStatuses[id]) {
      console.error(`Status with id ${id} not found.`);
      return statuses;
    }
    setCurrentCustomStatuses({
      ...currentCustomStatuses,
      [id]: {
        ...currentCustomStatuses[id],
        active: !currentCustomStatuses[id].active,
      },
    });
  };

  const handleTitleChange = (id: string, value: string) => {
    updateChangedIds(id);

    if (!currentCustomStatuses[id]) {
      console.error(`Status with id ${id} not found.`);
      return statuses;
    }
    setCurrentCustomStatuses({
      ...currentCustomStatuses,
      [id]: {
        ...currentCustomStatuses[id],
        title: value,
      },
    });

    if (value.length >= MAX_LENGTH) {
      setHelperTexts((prevHelperTexts) => ({ ...prevHelperTexts, [id]: `Maximum ${MAX_LENGTH} characters` }));
    } else {
      setHelperTexts((prevHelperTexts) => ({ ...prevHelperTexts, [id]: '' }));
    }
  };

  const handleSave = async () => {
    const hasEmptyTitles = Object.values(currentCustomStatuses).some(({ title }) => title.trim() === '');

    if (hasEmptyTitles) {
      alert('All status fields must be filled before saving.');
      return;
    }

    if (changedIds.length) {
      const updatedCustomStatuses = Object.values(currentCustomStatuses).filter((status: { id: string }) =>
        changedIds.includes(status.id),
      );

      updateCustomStatus({ updatedCustomStatuses });
      setChangedIds([]);

      // Track visibility changes
      for (const updatedStatus of updatedCustomStatuses) {
        const currentStatus = statuses?.find(({ id }) => id === updatedStatus.id);
        if (currentStatus && currentStatus.active !== updatedStatus.active) {
          tracker.track({
            name: 'status-visibility-change',
            data: {
              title: updatedStatus.title,
              type: updatedStatus.statusType,
              visible: updatedStatus.active ? 'visible' : 'hidden',
            },
          });
        }
      }
    }

    if (initialOrgData && initialOrgData?.forceReview !== currentForceReview) {
      const showSuccessMessage = !changedIds.length;
      await dispatch(
        updateOrganization({ forceReview: currentForceReview }, orgId, initialOrgData, showSuccessMessage, false),
      );
      await refreshOrgData();

      // Track force review changes
      tracker.track({
        name: 'force-review-change',
        data: {
          forceReview: currentForceReview ? 'enabled' : 'disabled',
        },
      });
    }
    setHelperTexts({});
  };

  const handleCancel = () => {
    setCurrentCustomStatuses(transformStatuses(statuses as CustomStatus[]));
    setCurrentForceReview(initialOrgData?.forceReview || false);
    setChangedIds([]);
    setHelperTexts({});
  };

  const handleForceReviewChange = () => {
    setCurrentForceReview(!currentForceReview);
  };

  if (isFetchingStatuses && isFetchingOrg) {
    return (
      <Paper className={classes.paper}>
        <CircularProgress size={30} />
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper}>
      <Grid container flexDirection="column" spacing={2}>
        <Grid item>
          <Typography variant="subtitle2">Statuses</Typography>
        </Grid>
        <Grid item>
          <Typography variant="body1">Customize your review statuses below</Typography>
        </Grid>
        <Grid item sx={{ marginTop: 'var(--gds-spacing-lg)' }}>
          {isFetchingStatuses ? (
            <div style={{ minHeight: '155px' }}>
              <CircularProgress size={15} />
            </div>
          ) : (
            <Grid container flexDirection="column" justifyContent="center">
              {Object.keys(currentCustomStatuses).map((id) => {
                const { active, title, statusType } = currentCustomStatuses[id];
                const { Icon, hotkey } = iconMap[statusType as keyof typeof iconMap] || {
                  Icon: ErrorOutlineOutlinedIcon,
                  hotkey: '-',
                };
                return (
                  <div key={id} className={styles.row} data-testid={`status-${statusType}`}>
                    <GVCheckbox
                      id={id}
                      checked={active}
                      className={classes.checkbox}
                      onChange={(e) => handleCheckboxChange(id)}
                    />
                    <Icon />
                    <GVTextField
                      className={styles.field}
                      label="Status title"
                      isLabelHidden
                      value={title}
                      onValueChange={(value) => handleTitleChange(id, value ?? '')}
                      maxLength={MAX_LENGTH}
                      message={helperTexts[id] || undefined}
                      hasError={Boolean(helperTexts[id])}
                    />
                    <Typography className={classes.hotkeyText} variant="body1">
                      Hotkey
                    </Typography>
                    <Box className={classes.hotkeyBox}>{hotkey}</Box>
                  </div>
                );
              })}
            </Grid>
          )}
        </Grid>
        <Grid item>
          <Typography variant="subtitle2">Report</Typography>
        </Grid>

        <Grid item container alignItems="center" sx={{ marginBottom: '8px' }}>
          <Grid item>
            {isFetchingOrg ? (
              <Grid item justifyContent={'center'}>
                <CircularProgress size={15} sx={{ float: 'left', marginRight: '12px' }} className={classes.checkbox} />
                <Typography
                  sx={{ width: '300px', color: '#FFFFFFDE', fontSize: '12px', marginTop: '11px', marginBottom: '9px' }}
                  variant="body1"
                >
                  Force review of results list
                </Typography>
              </Grid>
            ) : (
              <GVCheckbox
                label="Force review of results list"
                checked={currentForceReview}
                className={classes.checkbox}
                onChange={handleForceReviewChange}
              />
            )}
          </Grid>
        </Grid>
        <Grid item className={classes.statusRequiredGridItem}>
          <Typography variant="body1" className={classes.secondaryText}>
            Users must view all differences in the results list before being permitted to generate a report
          </Typography>
        </Grid>
      </Grid>
      <Grid item className={classes.buttonContainer}>
        <Button variant="contained" color="secondary" onClick={handleSave} disabled={!unsavedChanges || isFetchingOrg}>
          Save
        </Button>
        <Button
          variant="outlined"
          color="primary"
          sx={{ background: 'transparent' }}
          onClick={handleCancel}
          disabled={!unsavedChanges || isFetchingOrg}
        >
          Cancel
        </Button>
      </Grid>
    </Paper>
  );
};

export default CustomStatuses;
